import { Polygon, Segment, Side, circularNext } from './polygon';
import {
    Point2D, Axis, comparePoint2D, orientation,
    intersection, IntersectionType, closeNumber,
    PROXIMITY
} from './geometry';
import { BinarySearchTree } from '../util/binary-search-tree';

let BALANCE_EVENTS = false;
let BALANCE_SWEEP_LINE = false;
let TRACE = false;
let LOG_VERTICES = true;
let LOG_DISTRIBUTION = false;
let ENABLE_DEBUG = false;
let DEBUG = false;
let DRAW_LINE: ( vertices: Point2D[], width: number, color?: string, showEnding?: boolean ) => void;
let IN_DEBUG_BOUNDS: ( p: Point2D ) => boolean;
let DEFAULT_DEBUG_LINE_WIDTH = 1;
let DRAW_ALL_EDGES = false;

let SETS: SetBuilder = undefined;

export const buildGraph = ( polygons: Polygon[], included: number[], excluded: number[], axis = Axis.Y, accuracy = 1 ): Polygon => {

    if ( SETS === undefined ) {
        SETS = new SetBuilder( polygons, axis, accuracy );
    }

    return SETS.getComplement( excluded, included );
}

export type SetSelector = ( setsAtLineSide: BinarySearchTree<number> ) => boolean;
export class SetBuilder {

    private setLines: SetLine[];
    private setIndices: number[];

    constructor( polygons: Polygon[], axis = Axis.Y, accuracy = 1 ) {
        let vertices = new VertexSet( axis );
        let t0 = Date.now();

        polygons.forEach( ( polygon, index ) => {
            polygon.paths.forEach( path => {
                addPath( accuracy, vertices, path, polygon.vertices, true, axis, index );
            } );
        } );
        let t1 = Date.now();

        if ( BALANCE_EVENTS ) vertices.balance();

        let t2 = Date.now();

        let addCount = vertices.getAddCount();
        let storeCount = vertices.getStoreCount();

        let sweepLine = new SweepLine( axis );
        let vertex: Vertex;
        while ( ( vertex = vertices.next() ) !== undefined ) {
            sweepLine.process( vertex, vertices );
        }
        let t3 = Date.now();

        if ( TRACE ) {
            console.log( `Number of vertices added is ${addCount} and of those ${storeCount} are sufficiently unique.` );
            console.log( 'Created events in %s ms.', t1 - t0 );
            console.log( 'Balanced events tree in %s ms.', t2 - t1 );
            console.log( 'Found lines using %s iterations in  %s ms.', sweepLine.processingCount, t3 - t2 );
            console.log( 'Analysed sets in %s ms.', t3 - t0 );
        }

        this.setLines = sweepLine.lines;
        this.setIndices = [...Array( polygons.length ).keys()];
    }

    public getUnion( included?: number[] ): Polygon {
        return this.getSet( SetBuilder.union( included ? included : this.setIndices ) );
    }
    public getIntersection( included?: number[] ): Polygon {
        return this.getSet( SetBuilder.intersection( included ? included : this.setIndices ) );
    }
    public getComplement( excluded?: number[], included?: number[], ): Polygon {
        return this.getSet( SetBuilder.complement( included ? included : this.setIndices, excluded ? excluded : [] ) );
    }
    public getSymetric( set0: number[], set1: number[] ): Polygon {
        return this.getSet( SetBuilder.symetric( set0, set1 ) );
    }

    public getSet( inSet: SetSelector ): Polygon {
        let t0 = Date.now();

        let edges = SetBuilder.getEdges( inSet, this.setLines );

        let t1 = Date.now();

        const polygon = SetBuilder.buildPolygon( edges.edges, edges.start );

        let t2 = Date.now();
        if ( TRACE ) {
            console.log( 'Extracted set lines in %s ms.', t1 - t0 );
            console.log( 'Constructed set in %s ms.', t2 - t1 );
            console.log( 'Total running time is %s ms.', t2 - t0 );
        }
        return polygon;
    }

    private static getEdges( inSet: SetSelector, lines: SetLine[] ): { edges: Map<Symbol, Point2D[]>, start: Symbol } {
        let edges = new Map<Symbol, Point2D[]>();
        let start: Symbol = undefined;
        let highestLevel: number = undefined;
        let minInSet: boolean;
        let maxInSet: boolean;
        let reverse: boolean;

        lines
            .forEach( line => {
                let edge: Point2D[];
                let edgeKey: Symbol;
                minInSet = inSet( line.minSets );
                maxInSet = inSet( line.maxSets );

                //a line is an edge if one side is part of the set and the other side is not.
                if ( minInSet != maxInSet ) {
                    // in order to produce CCW results we need to know if the line must be read in reverse order
                    // The line always goes downward (when axis is Y: leftwards when axis is X, in either case 
                    // in descending direction) so if the set lies left from the line we need to traverse from 
                    // bottom to top( ascending direction), hence read vertices in reverse order. When the line
                    // is no edge it does not really matter because we will not be using the line in constructing
                    // the polygon(s) that define our set.
                    edge = minInSet ? line.path.slice().reverse() : line.path.slice();
                    edgeKey = Vertex.getKey( edge[0] );
                    edges.set( edgeKey, edge );
                    if ( highestLevel === undefined || line.level > highestLevel ) {
                        highestLevel = line.level;
                        start = edgeKey;
                    }
                }
            } );

        return { edges: edges, start: start };

    }
    private static buildPolygon( edges: Map<Symbol, Point2D[]>, start: Symbol ): Polygon {
        if ( start ) {
            let polygonVertices: Point2D[] = [];
            let polygonPath: number[] = [];
            let polygonPaths: number[][] = [polygonPath];

            let pathStart = start;
            let line: Point2D[];
            let polyVertexIndex = 0;

            while ( edges.size > 0 ) {
                line = edges.get( pathStart );
                edges.delete( pathStart );
                if ( line !== undefined ) {
                    line.forEach( vertex => {
                        polygonVertices.push( vertex );
                        polygonPath.push( polygonVertices.length - 1 );
                    } )
                    //last vertex is the start of the next path and should not be added to the path
                    pathStart = Vertex.getKey( polygonVertices.pop() );
                    polygonPath.pop();
                } else {
                    pathStart = edges.keys().next().value;
                    polygonPath = [];
                    polygonPaths.push( polygonPath );
                }
            }
            return new Polygon( polygonVertices, polygonPaths );
        } else {
            return undefined;
        }
    }
    public static union = ( sets: number[] ): SetSelector => {
        return SetBuilder.someIn( sets );
    }
    public static intersection = ( sets: number[] ): SetSelector => {
        return SetBuilder.allIn( sets );
    }
    public static complement = ( includedSets: number[], excludedSets: number[] ): SetSelector => {
        const included = SetBuilder.someIn( includedSets );
        const excluded = SetBuilder.allOut( excludedSets );
        return ( setsAtLineSide: BinarySearchTree<number> ) => {
            return included( setsAtLineSide ) && excluded( setsAtLineSide );
        }
    }
    public static symetric = ( sets0: number[], sets1: number[] ): SetSelector => {
        const complement0 = SetBuilder.complement( sets0, sets1 );
        const complement1 = SetBuilder.complement( sets1, sets0 );
        return ( setsAtLineSide: BinarySearchTree<number> ) => {
            return complement0( setsAtLineSide ) || complement1( setsAtLineSide );
        }
    }
    private static someIn( includedSets: number[] ): SetSelector {
        const lInc = includedSets.length;
        return ( setsAtLineSide: BinarySearchTree <number>) => {
            let oneIncludedFound = false;
            let i = 0;
            while ( !oneIncludedFound && i < lInc ) {
                oneIncludedFound = setsAtLineSide.has( includedSets[i++] );
            }
            return oneIncludedFound;
        }
    }
    private static allIn( includedSets: number[] ): SetSelector {
        const lInc = includedSets.length;
        return ( setsAtLineSide: BinarySearchTree<number> ) => {
            let allIncludedFound = true;
            let i = 0;
            while ( allIncludedFound && i < lInc ) {
                allIncludedFound = allIncludedFound && setsAtLineSide.has( includedSets[i++] );
            }
            return allIncludedFound;
        }
    }
    private static allOut( excludedSets: number[] ): SetSelector {
        const lExc = excludedSets.length;
        return ( setsAtLineSide: BinarySearchTree<number> ) => {
            let allExcludedNotFound = true;
            let i = 0;
            while ( allExcludedNotFound && i < lExc ) {
                allExcludedNotFound = allExcludedNotFound && !setsAtLineSide.has( excludedSets[i++] );
            }
            return allExcludedNotFound;
        }
    }

}

interface SetLine { path: Point2D[], level: number, minSets: BinarySearchTree<number>, maxSets: BinarySearchTree<number> };
class SweepLine {
    public position: Point2D;
    public line: BinarySearchTree<Step>;
    public lines: SetLine[];
    public processingCount: number;
    public comparator: ( line0: Step, line1: Step ) => number;

    constructor( public axis: Axis ) {
        this.comparator = Step.comparator.bind( this, this.axis );
        this.line = new BinarySearchTree( this.comparator );
        this.lines = [];
        this.processingCount = 0;
    }

    public process( vertex: Vertex, vertices: VertexSet ): void {
        let id: Symbol;
        let step: Step;
        let arrival: Step;
        let minArrival: Step;
        let maxArrival: Step;
        let arrivalsPredecessorStep: Step;
        let arrivalsSuccessorStep: Step;
        let departure: Step;
        let minDeparture: Step;
        let maxDeparture: Step;
        let departuresPredecessorStep: Step;
        let departuresSuccessorStep: Step;

        let edgeArrivalStep: Step;
        let intersectionVertex = false;

        let lineArrivalStep: Step;

        let linePassingThrough = vertex.arrivals.size() === 1 && vertex.departures.size() === 1;

        vertex.order();

        for ( arrival of vertex.arrivals.inOrder() ) {
            if ( step !== undefined ) {
                this.line.delete( step );
            }
            if ( arrival.newIntersection ) {
                intersectionVertex = true;
            }
            if ( minArrival === undefined ) {
                minArrival = arrival;
                arrivalsPredecessorStep = this.line.prev( arrival );
            }
            arrival.addVertexToLineString( vertex.value, this.axis );
            if ( linePassingThrough ) {
                lineArrivalStep = arrival;
            } else {
                //not only one line passing through so stop tracing and add to line map
                this.addLineString( arrival );
            }
            arrival.newIntersection = false;

            step = arrival;

        }
        maxArrival = arrival;
        arrivalsSuccessorStep = arrival ? this.line.next( arrival ) : undefined;
        if ( arrival ) {
            this.line.delete( arrival );
        }

        step = undefined;
        for ( departure of vertex.departures.inOrder() ) {
            departure.sweepLinePosition = vertex;
            if ( departure.sweepLinePosition.id !== departure.end.id ) {
                this.line.add( departure );
                if ( minDeparture === undefined ) {
                    minDeparture = departure;
                    departuresPredecessorStep = this.line.prev( departure );
                }
            } else {
                //console.warn( 'NOT adding a zero length departure.' );
            }

            if ( linePassingThrough ) {
                //only one line passing through so continue tracing
                departure.setLines( lineArrivalStep );
                lineArrivalStep = undefined;
            } else {
                //start of new line;
                departure.addVertexToLineString( departure.sweepLinePosition.value, this.axis );
            }

        }
        maxDeparture = departure;
        departuresSuccessorStep = departure ? this.line.next( departure ) : undefined;

        if ( this.processingCount % 10 === 0 ) {
            if ( BALANCE_SWEEP_LINE ) this.line.balance( false );
        }

        //update sets
        this.distributeSets();

        //test for intersections
        if ( vertex.departures.size() > 0 ) {
            if ( departuresPredecessorStep ) {
                this.testForIntersection( departuresPredecessorStep, minDeparture, vertices );
            }
            if ( departuresSuccessorStep ) {
                this.testForIntersection( maxDeparture, departuresSuccessorStep, vertices );
            }
        } else {
            if ( arrivalsPredecessorStep && arrivalsSuccessorStep ) {
                this.testForIntersection( arrivalsPredecessorStep, arrivalsSuccessorStep, vertices );
            }
        }
        if ( this.processingCount % 100 === 0 ) {
            if ( BALANCE_EVENTS ) vertices.set.balance( false );
        }

        if ( intersectionVertex ) {
            vertex.reset();
        }
        this.processingCount++;
    }

    private distributeSets(): void {
        let set = new BinarySearchTree( numberComparator );
        let line: Step;
        for ( line of this.line.inOrder() ) {
            line.minSets.clear().addAll( set );
            set.deleteAll( line.minSetBoundaries );
            set.addAll( line.maxSetBoundaries );
            line.maxSets.clear().addAll( set );
        }

    }

    private testForIntersection( line0: Step, line1: Step, vertices: VertexSet ): Vertex {

        let intersectionResult = intersection(
            line0.start.value,
            line0.end.value,
            line1.start.value,
            line1.end.value );

        if ( intersectionResult.type === IntersectionType.NORMAL || intersectionResult.type === IntersectionType.VERTEX ) {

            let iVertex = undefined;
            let isNew0 = true;
            let isNew1 = true;
            if ( intersectionResult.type === IntersectionType.NORMAL ) {

                if ( ( isNew0 = !line0.hasIntersection( intersectionResult.t0 ) ) ) {
                    iVertex = iVertex === undefined ? vertices.add( intersectionResult.i0 ) : iVertex;
                    line0.intersect( intersectionResult.t0, iVertex );
                }

                if ( ( isNew1 = !line1.hasIntersection( intersectionResult.t1 ) ) ) {
                    iVertex = iVertex === undefined ? vertices.add( intersectionResult.i0 ) : iVertex;
                    line1.intersect( intersectionResult.t1, iVertex );
                }

            } else {
                //the intersection lies on one of the vMin vertices
                if ( intersectionResult.t0 === 1 ) {
                    //line 0 cuts line1
                    //cut line1 at point of intersection
                    if ( ( isNew1 = !line1.hasIntersection( intersectionResult.t1 ) ) ) {
                        iVertex = iVertex === undefined ? vertices.add( intersectionResult.i0 ) : iVertex;
                        line1.intersect( intersectionResult.t1, iVertex );
                    }

                } else if ( intersectionResult.t1 === 1 ) {
                    //line 1 cuts line0
                    //cut line0 at point of intersection
                    if ( ( isNew0 = !line0.hasIntersection( intersectionResult.t0 ) ) ) {
                        iVertex = iVertex === undefined ? vertices.add( intersectionResult.i0 ) : iVertex;
                        line0.intersect( intersectionResult.t0, iVertex );
                    }
                }
            }

            return iVertex;
        }
        return undefined;
    }


    private addLineString( step: Step ): boolean {
        let lineString = step.lineVertices();
        if ( lineString && lineString.length >= 2 ) {
            let line = {
                path: lineString,
                level: step.maxLineVertexAxisValue,
                minSets: new BinarySearchTree( numberComparator, undefined, step.minSets ),
                maxSets: new BinarySearchTree( numberComparator, undefined, step.maxSets )
            };

            this.lines.push( line );

            step.clearLines();
            return true;
        } else {
            //console.warn( 'A call to addLineString with an lineString with fewer than 2 vertices.' );
            return false
        }
    }
}

interface StepIndex {
    start: Vertex,
    end: Vertex
}
class Step implements StepIndex {
    public static comparator = ( axis: Axis, line0: Step, line1: Step ) => {
        if ( line0.sweepLinePosition.id === line1.sweepLinePosition.id && line0.end.id === line1.end.id ) { //same step end points so same line
            return 0;
        } else {
            const vMax0 = line0.sweepLinePosition.value;
            const vMax1 = line1.sweepLinePosition.value;
            const vMin0 = line0.end.value;
            const vMin1 = line1.end.value;

            let segm: Segment;
            let vMax: Point2D;
            let vMin: Point2D;
            let vertex: Point2D;
            let otherVertex: Point2D;
            let orientationResult: number //the cross product result used during this test
            //when the segment comes first, return this result
            let resultForSegment: number;
            //when the vertex comes first, return this result
            let resultForVertex: number;

            //determine which line has the highest axis vertex coordinate.
            //the line with the highest coordinate will supply the segment for the tests, while
            //the lowest line will supply the vertex and the lowest vertex for the tests
            let positionDiff = vMax0[<number>axis] - vMax1[<number>axis];
            if ( positionDiff === 0 ) {
                // line0 and line1 have the same top coordinate on the axis.
                if ( vMax0[<number>axis] === vMin0[<number>axis] ) {//line 0 is horizontal
                    return horizantalTest( vMax0, vMin0, vMax1, vMin1, -1, 1, axis );
                } else if ( vMax1[<number>axis] === vMin1[<number>axis] ) {//line 1 is horizontal
                    return horizantalTest( vMax1, vMin1, vMax0, vMin0, 1, -1, axis );
                } else {
                    //Test their [1-axis] coordinate. Line with lowest comes first 
                    let diff = vMax0[1 - axis] - vMax1[1 - axis];
                    if ( diff !== 0 ) {
                        return diff;
                    } else {
                        //vertex line 0 equals vertex line 1, look at the line's lower vertices using a cross product test
                        orientationResult = orientation( vMax0, vMin0, vMin1 );
                        if ( orientationResult === 0 ) {
                            //the two lines are collinear
                            return collinearTest( vMax0, vMin0, vMax1, vMin1, axis );
                        } else {
                            //orientation <0 (CW), line1 comes first
                            //orientation >0 (CCW), line 0 comes first
                            return orientationResult < 0 ? 1 : -1;
                        }
                    }
                }
            } else {
                if ( positionDiff < 0 ) {
                    //line1 lies above line0 -> line1 supplies the step; line0 supplies the vertex
                    vMax = vMax1;
                    vMin = vMin1;
                    vertex = vMax0;
                    otherVertex = vMin0;
                    resultForSegment = 1;
                    resultForVertex = -1;
                } else if ( positionDiff > 0 ) {
                    //line0 lies above line1 -> line0 supplies the step; line1 supplies the vertex
                    vMax = vMax0;
                    vMin = vMin0;
                    vertex = vMax1;
                    otherVertex = vMin1;
                    resultForSegment = -1;
                    resultForVertex = 1;
                }
                //extract the low and high vertices of the segment 

                orientationResult = orientation( vMin, vMax, vertex );
                if ( orientationResult > 0 ) {
                    //vertex lies left from segment: the line with the vertex comes first
                    return resultForVertex;
                } else if ( orientationResult < 0 ) {
                    // vertex lies right from segment: the line with the segment comes first
                    return resultForSegment;
                } else {
                    //vertex and segment are collinear: repeat test with other vertex
                    orientationResult = orientation( vMin, vMax, otherVertex );
                    if ( orientationResult > 0 ) {
                        // other vertex lies left from segment: the line with the vertex comes first
                        return resultForVertex;
                    } else if ( orientationResult < 0 ) {
                        // other vertex lies right from segment: the line with the segment comes first
                        return resultForSegment;
                    } else {
                        //lines overlap, decide on something else: the polygon number
                        return collinearTest( vMax, vMin, vertex, otherVertex, axis );
                    }
                }
            }
        }
    }

    public id: Symbol;
    public minSets: BinarySearchTree<number>;
    public maxSets: BinarySearchTree<number>;
    public minSetBoundaries: number[];
    public maxSetBoundaries: number[];

    private lineString: Point2D[];
    public maxLineVertexAxisValue: number;

    public sweepLinePosition: Vertex;
    public intersections: Map<number, Point2D>;
    public newIntersection: boolean;

    public static from( start: Vertex, end: Vertex, setId: number, inside: Side ): Step {
        return new Step( start, end ).addSet( setId, inside );
    }

    private constructor( public start: Vertex, public end: Vertex ) {
        this.id = Symbol.for( `step[${JSON.stringify( this.start.value )},${JSON.stringify( this.end.value )}]` );
        this.minSets = new BinarySearchTree( numberComparator );
        this.maxSets = new BinarySearchTree( numberComparator );
        this.minSetBoundaries = [];
        this.maxSetBoundaries = [];
        this.sweepLinePosition = start;
        this.newIntersection = false;
    }

    public addSet( setId: number, inside: Side ): Step {
        if ( inside === Side.LEFT ) {
            this.minSetBoundaries.push( setId );
        } else {
            this.maxSetBoundaries.push( setId );
        }
        return this;
    }
    public toString(): string {
        return `${JSON.stringify( this.sweepLinePosition.value )}/${JSON.stringify( this.end.value )}`;
    }

    public toFullString(): string {
        return `line[ ${JSON.stringify( this.start.value )}, ${JSON.stringify( this.end.value )} ] ${this.minSets.size() > 0 ? Array.from( this.minSets.inOrder() ).join( ',' ) : '-'} |  ${this.maxSets.size() > 0 ? Array.from( this.maxSets.inOrder() ).join( ',' ) : '-'}`;
    }
    public hasIntersection( t: number ): boolean {
        return this.intersections !== undefined && this.intersections.get( t ) !== undefined;
    }
    private addIntersection( t: number, p: Point2D ): boolean {
        if ( this.intersections === undefined ) {
            this.intersections = new Map();
        }

        if ( this.intersections.get( t ) === undefined ) {
            this.intersections.set( t, p );
            return true;
        } else {
            return false;
        }
    }
    public addVertexToLineString( vertex: Point2D, axis: Axis ): Step {
        if ( this.lineString === undefined ) {
            this.lineString = [vertex];
            this.maxLineVertexAxisValue = vertex[<number>axis];
        } else {
            this.lineString.push( vertex );
            if ( this.maxLineVertexAxisValue < vertex[<number>axis] ) {
                this.maxLineVertexAxisValue = vertex[<number>axis];
            }
        }
        return this;
    }
    public setLines( connectingStep: Step ): Step {
        this.lineString = connectingStep.lineString;
        this.maxLineVertexAxisValue = connectingStep.maxLineVertexAxisValue;
        return this;
    }
    public clearLines(): Step {
        this.lineString = undefined;
        this.maxLineVertexAxisValue = undefined;
        return this;
    }
    public lineVertices(): Point2D[] {
        return this.lineString && this.lineString.length > 0 ? this.lineString.slice() : undefined;
    }

    public intersect( t: number, vertex: Vertex ): boolean {
        if ( this.addIntersection( t, vertex.value ) ) {
            this.newIntersection = true;

            this.start.departuresMap.delete( this.end.id );
            this.end.arrivalsMap.delete( this.start.id );

            vertex.arrivalsMap.set( this.start.id, this );
            this.start.departuresMap.set( vertex.id, this );

            vertex.departuresMap.set( this.end.id, this );
            this.end.arrivalsMap.set( vertex.id, this );
            return true;
        } else {
            return false;
        }
    }
}

class VertexSet {
    private static unboundSetComparator = ( axis: Axis, vertex0: VertexIndex, vertex1: VertexIndex ): number => -comparePoint2D( vertex0.value, vertex1.value, axis );
    private static unboundComparator = ( axis: Axis, vertex0: VertexIndex, vertex1: VertexIndex ): number => comparePoint2D( vertex0.value, vertex1.value, axis );

    public compare: ( vertex0: VertexIndex, vertex1: VertexIndex ) => number;
    public setCompare: ( vertex0: VertexIndex, vertex1: VertexIndex ) => number;

    public set: BinarySearchTree<VertexIndex>;

    public addCount: number;

    constructor( public axis = Axis.Y ) {
        this.compare = VertexSet.unboundComparator.bind( this, axis );
        this.setCompare = VertexSet.unboundSetComparator.bind( this, axis );
        this.set = new BinarySearchTree( this.setCompare );
        this.addCount = 0;
    }

    public getAddCount(): number {
        return this.addCount;
    }
    public getStoreCount(): number {
        return this.set.size();
    }
    public add( p: Point2D ): Vertex {
        this.addCount++;
        let storedV: Vertex = <Vertex>this.set.get( { value: p } );
        if ( storedV === undefined ) {
            return <Vertex>this.set.add( Vertex.from( p, this.axis ) ).data;
        } else {
            return storedV;
        }
    }

    public isEmpty(): boolean {
        return this.set.isEmpty();
    }

    public next(): Vertex {
        const vertex: Vertex = <Vertex>this.set.min();
        if ( vertex ) {
            this.set.delete( vertex );
        }
        return vertex;
    }

    public balance(): VertexSet {
        this.set.balance();
        return this;
    }

    public log( count: number ) {
        let i = count;
        let iter = this.set.inOrder();
        let vertStrings: string[] = [];
        while ( i > 0 ) {
            i--;
            let v: Vertex = iter.next().value;
            vertStrings.push( v === undefined ? '-' : JSON.stringify( v.value ) );
        }
        console.log( `EVENTS: ${vertStrings.join( ', ' )}` );
    }
}

interface VertexIndex {
    value: Point2D;
}
class Vertex implements VertexIndex {
    public static stepComparator( axis: Axis, shared: Point2D, isArrival: boolean, step0: Step, step1: Step ): number {
        let o = isArrival ? -orientation( step0.start.value, shared, step1.start.value ) : -orientation( shared, step0.end.value, step1.end.value );
        if ( o === 0 ) {
            return isArrival ? comparePoint2D( step0.start.value, step1.start.value, axis ) : -comparePoint2D( step0.end.value, step1.end.value, axis )
        } else {
            return o;
        }
    }

    public static from = ( v: Point2D, axis: Axis ): Vertex => {
        return new Vertex( v, axis );
    }

    public static getKey = ( p: Point2D ): Symbol => Symbol.for( JSON.stringify( p ) );

    public id: Symbol;
    public arrivalsMap: Map<Symbol, Step>;
    public departuresMap: Map<Symbol, Step>;

    public arrivals: BinarySearchTree<Step>;
    public departures: BinarySearchTree<Step>;

    private constructor( public value: Point2D, axis: Axis ) {
        this.id = Symbol.for( `vertex${JSON.stringify( this.value )}` );
        this.arrivalsMap = new Map();
        this.departuresMap = new Map();
        this.arrivals = new BinarySearchTree( Vertex.stepComparator.bind( this, axis, this.value, true ) );
        this.departures = new BinarySearchTree( Vertex.stepComparator.bind( this, axis, this.value, false ) );
    }

    public connectArrival( arriveFrom: Vertex, setId: number, inside: Side ): Step {
        let step: Step = this.arrivalsMap.get( arriveFrom.id );
        if ( step === undefined ) {
            //check if this step has already been registered at the starting point so we can reuse it
            step = arriveFrom.departuresMap.get( this.id );
            if ( step === undefined ) {
                //no we are the first, create the step
                step = Step.from( arriveFrom, this, setId, inside );
                //and add this step to the starting point
                arriveFrom.departuresMap.set( this.id, step );
            } else {
                step.addSet( setId, inside );
            }
            this.arrivalsMap.set( arriveFrom.id, step );
            return step;
        } else {
            //this step has been taken before by another set edge
            step.addSet( setId, inside );
            return step;
        }
    }
    public connectDeparture( departTo: Vertex, setId: number, inside: Side ): Step {
        let step: Step = this.departuresMap.get( departTo.id );
        if ( step === undefined ) {
            //check if this step has already been registered at the destination point so we can reuse it
            step = departTo.arrivalsMap.get( this.id );
            if ( step === undefined ) {
                //no we are the first, create the step
                step = Step.from( this, departTo, setId, inside );
                //and add this step to the destination point
                departTo.arrivalsMap.set( this.id, step );
            } else {
                step.addSet( setId, inside );
            }
            this.departuresMap.set( departTo.id, step );
            return step;
        } else {
            step.addSet( setId, inside );
            return step;
        }
    }
    public toString(): string {
        return `VERTEX${JSON.stringify( this.value )} A:[ ${Array.from( this.arrivalsMap.values() ).map( step => JSON.stringify( step.start.value ) ).join( ', ' )} ] D:[ ${Array.from( this.departuresMap.values() ).map( step => JSON.stringify( step.end.value ) ).join( ', ' )} ]`;
    }

    public reset(): void {
        this.arrivalsMap.clear();
        this.departuresMap.clear();
        this.arrivals.clear();
        this.departures.clear();
    }

    public order(): void {
        if ( this.arrivals.size() + this.departures.size() === 0 ) {
            let step: Step
            for ( step of this.arrivalsMap.values() ) this.arrivals.add( step );
            for ( step of this.departuresMap.values() ) this.departures.add( step );

        }
    }
}

const snap = ( accuracy: number, vertex: Point2D ): Point2D => [Math.round( vertex[0] / accuracy ) * accuracy, Math.round( vertex[1] / accuracy ) * accuracy];
const numberComparator = ( n0: number, n1: number ) => n0 - n1;
const nToS = ( val: number, undefinedValue?: string, width?: number ): string => {
    const w = 3;
    return val !== undefined ?
        val.toString().padStart( width === undefined ? w : width, '0' ) :
        ( undefinedValue ? undefinedValue : '-' ).padStart( width === undefined ? w : width, ( undefinedValue ? undefinedValue : '-' ) );
}
const getUniqueLines = ( lines: Point2D[][], accuracy: number ): Point2D[][] => {
    let uniqueLines: Point2D[][] = [];
    for ( let i = 0; i < lines.length; i++ ) {
        let isUnique = true;
        let j = i + 1;
        while ( j < lines.length && !equalLines( lines[i], lines[j], accuracy ) ) j++;
        if ( j >= lines.length ) {
            uniqueLines.push( lines[i] );
        }
    }
    return uniqueLines;
}
const equalLines = ( line0: Point2D[], line1: Point2D[], accuracy: number ): boolean => {
    return ( line0.length === line1.length && equalUntil( line0, line1, accuracy ) === line0.length );
}
const equalUntil = ( line0: Point2D[], line1: Point2D[], accuracy: number ): number => {
    let i = 0;
    while ( i < line0.length && i < line1.length && comparePoint2D( line0[i], line1[i], Axis.Y, accuracy ) === 0 ) i++;
    return i;
}

const addPath = ( accuracy: number, verts: VertexSet, path: number[], vertices: Point2D[], circular: boolean, axis = Axis.Y, setId = 0 ): any => {
    let start: Vertex = undefined;
    let end: Vertex = undefined;
    let vertex0: Vertex = undefined;
    let vertex1: Vertex = undefined;

    path.forEach( ( iV0, iPath, path ) => {
        if ( circular || iPath < path.length - 1 ) {
            const iV1 = path[circularNext( iPath, path )];

            if ( vertex1 === undefined ) {
                vertex0 = verts.add( snap( accuracy, vertices[iV0] ) );
            } else {
                vertex0 = vertex1;
            }
            vertex1 = verts.add( snap( accuracy, vertices[iV1] ) );
            start = vertex0;
            end = vertex1;
            let inside = Side.RIGHT;
            if ( circular ) {
                if ( verts.compare( start, end ) < 0 ) {
                    const swap = start;
                    start = end;
                    end = swap;
                    inside = Side.LEFT;
                }
            }
            //link the two vertices
            start.connectDeparture( end, setId, inside );
        }
    } );
}

const horizantalTest = ( vMax0: Point2D, vMin0: Point2D, vMax1: Point2D, vMin1: Point2D, result0: number, result1: number, axis: Axis ): number => {
    //line0 is 'horizontal' (when axis is Axis.Y, other wise it is vertical but treatment is the same)
    //one of the vertices of line 1 is collinear with line 0
    if ( Math.max( vMax0[1 - axis], vMin0[1 - axis] ) <= vMax1[1 - axis] ) {
        //line 0 lies completely before line1
        return result0;
    } else if ( vMax1[1 - axis] <= Math.min( vMax0[1 - axis], vMin0[1 - axis] ) ) {
        //line 0 lies completely after line1
        return result1;
    } else if ( vMax1[<number>axis] === vMin1[<number>axis] ) {
        //mmmmmm line1 is also horizontal (so the lines are collinear and horizontal)
        if ( Math.min( vMax0[1 - axis], vMin0[1 - axis] ) < Math.min( vMax1[1 - axis], vMin1[1 - axis] ) ) {
            //line 0 started first so comes first
            return result0;
        } else if ( Math.min( vMax0[1 - axis], vMin0[1 - axis] ) > Math.min( vMax1[1 - axis], vMin1[1 - axis] ) ) {
            return result1;
        } else {
            //ok line0 and line1 match perfectly (which should not happen in this implementation)
            console.error( 'Comparing two identical steps which should not happen here.' );
            return 0;
        }
    } else {
        return result0;
    }
}
const collinearTest = ( vMax0: Point2D, vMin0: Point2D, vMax1: Point2D, vMin1: Point2D, axis: Axis ): number => {
    let positionDiff = vMax1[<number>axis] - vMax0[<number>axis];
    if ( positionDiff !== 0 ) {
        return positionDiff;
    } else {
        //choose the one that ends first
        positionDiff = vMin1[<number>axis] - vMin0[<number>axis];
        if ( positionDiff !== 0 ) {
            return positionDiff;
        } else {
            //ok line0 and line1 match perfectly (which should not happen in this implementation)
            console.error( 'Comparing two identical steps which should not happen here.' );
            return 0;
        }
    }
}
